import { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';

// Injected at build time by vite.config.ts
declare const __APP_VERSION__: string;

const CHECK_INTERVAL_MS = 5 * 60 * 1000; // Check every 5 minutes

interface VersionInfo {
  version: string;
  buildTime: string;
}

/**
 * Version checker component - detects when a new version is available
 * and prompts user to reload the app
 */
export function VersionChecker() {
  const [newVersionAvailable, setNewVersionAvailable] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const checkVersion = async () => {
      try {
        // Add cache-busting query param
        const response = await fetch(`/version.json?_=${Date.now()}`, {
          cache: 'no-store',
        });
        
        if (!response.ok) return;
        
        const data: VersionInfo = await response.json();
        
        // Compare versions
        if (data.version && data.version !== __APP_VERSION__ && isMounted) {
          console.log(`New version available: ${data.version} (current: ${__APP_VERSION__})`);
          setNewVersionAvailable(true);
        }
      } catch (error) {
        // Silently fail - version check is not critical
        console.debug('Version check failed:', error);
      }
    };

    // Check immediately on mount
    checkVersion();

    // Then check periodically
    const interval = setInterval(checkVersion, CHECK_INTERVAL_MS);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  const handleReload = () => {
    // Force reload without cache
    window.location.reload();
  };

  if (!newVersionAvailable) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 z-[100] animate-slide-up">
      <div className="bg-primary rounded-xl shadow-2xl p-4 flex items-center justify-between gap-3 max-w-md mx-auto">
        <div className="flex items-center gap-3">
          <RefreshCw className="w-5 h-5 text-white animate-spin-slow" />
          <div>
            <p className="text-white font-medium text-sm">Update Available</p>
            <p className="text-white/80 text-xs">Tap to get the latest version</p>
          </div>
        </div>
        <button
          onClick={handleReload}
          className="px-4 py-2 bg-white text-primary font-semibold rounded-lg text-sm hover:bg-white/90 transition-colors"
        >
          Update
        </button>
      </div>
    </div>
  );
}

export default VersionChecker;
